import moment from 'moment-timezone';

const iso8601DateFormat = 'YYYY-MM-DD';
const iso8601DateTimeFormat = 'YYYY-MM-DDTHHmm';
const militaryTimeFormat = 'HHmm';

export function toLocalDate(input) {
    if (input instanceof Date) {
        return moment().set(input).format(iso8601DateFormat);
    }
    else if (moment.isMoment(input)) {
        return input.format(iso8601DateFormat);
    }
    else if (!input) {
        return input;
    }

    throw new Error("Invalid parameter.  Required: moment or Date.");
}

export function toLocalTime(input) {
    if (input instanceof Date) {
        return moment().set(input).format(militaryTimeFormat);
    }
    else if (moment.isMoment(input)) {
        return input.format(militaryTimeFormat);
    }
    else if (!input) {
        return input;
    }

    throw new Error("Invalid parameter.  Required: moment or Date.");
}

export function toMoment(localDate, localTime = "0000", timeZone) {
    if (timeZone) {
        return moment.tz(`${localDate}T${localTime}`, iso8601DateTimeFormat, timeZone);
    }
    return moment(`${localDate}T${localTime}`, iso8601DateTimeFormat);
}

export function localDateNow() {
    return toLocalDate(moment());
}

export function getMoment(timeZone){
    return moment().tz(timeZone)
}

export function formatUsLongDate(localDate) {
    return moment(localDate).format('MMM D, YYYY');
}


export function formatUsLongDateTime(localDate) {
    return moment(localDate).format('MMM D, YYYY | hh:mm A');
}

export function formatUsShortDate(localDate) {
    return moment(localDate).format('MM/DD/YY');
}

export function formatUsMonthAndDay(localDate) {
  return moment(localDate).format('MMM D');
}

export function formatUsShortDateTime(localDate) {
    return moment(localDate).format('MM/DD/YY | hh:mm A');
}

/**
 * @param localDate A date in yyyy-mm-dd format, e.g. 2018-05-18
 * @returns {string} The date in ddd, MMM D, YYYY format, e.g. Sun, May 27, 2018
 */
export function getLongDateWithWeekday(localDate) {
    return moment(localDate).format('ddd, MMM D, YYYY');
}

/**
 * @param localDate A date in yyyy-mm-dd format, e.g. 2018-10-01
 * @returns {string} The year in yyyy format, e.g. 2018
 */
export function getYear(localDate) {
    return localDate.split('-')[0];
}

/**
 * @param localDate A date in yyyy-mm-dd format, e.g. 2018-10-01
 * @returns {string} The month in mm format, e.g. 10
 */
export function getMonth(localDate) {
    return localDate.split('-')[1];
}
/**
 * @param localDate A date in yyyy-mm-dd format, e.g. 2018-10-01
 * @returns {string} The day in dd format, e.g. 01
 */
export function getDay(localDate) {
    return localDate.split('-')[2];
}

export function toAmPmTime(militaryTime) {
    let result = militaryTime;
    if (/^\d{4}$/.test(militaryTime)) { // If exactly 4 digits
        let hours = parseInt(militaryTime.substring(0, 2), 10);
        hours %= 24; //
        const ampm = hours < 12 ? " AM" : " PM";
        if (hours > 12) {
            hours -= 12;
        }
        else if (hours === 0) {
            hours = 12;
        }
        result = `${hours}:${militaryTime.substring(2)}${ampm}`;
    }
    return result;
}


/**
 *
 * @param {string} standardTime Time in HH:MM AM/PM format, e.g., '01:23 PM'
 * @returns {string} Time in military format, e.g., '1:23 PM' becomes '13:23'
 */
export function toMilitaryTime(standardTime) {
    const regex = /\b((1[0-2]|0?[0-9]):([0-5][0-9]) ([AaPp][Mm]))/;
    let result = standardTime;

    if (regex.test(standardTime)) {
        let hours = Number(standardTime.match(/^(\d+)/)[1]);
        const minutes = Number(standardTime.match(/:(\d+)/)[1]);
        const AMPM = standardTime.match(/\s(.*)$/)[1];

        if (AMPM === 'AM' && hours === 12) {
            hours -= 12;
        }
        else if (AMPM === 'PM' && hours < 12) {
            hours += 12;
        }

        let stringHours = hours.toString();
        let stringMinutes = minutes.toString();

        if (hours < 10) {
            stringHours = `0${stringHours}`;
        }
        if (minutes < 10) {
            stringMinutes = `0${stringMinutes}`;
        }

        result = `${stringHours}:${stringMinutes}`;
    }

    return result;
}

/**
 *
 * @param {string} startTime the starting time as a timestamp e.g., "2018-08-08T04:00:00Z"
 * @param {string} endTime the end time as a timestamp e.g., "2018-08-09T21:00:00Z"
 * @returns {boolean} True if now is equal to or after startTime and before endTime
 */
export function isNowBetween(startTime, endTime) {
    // Moment.js 2.13.0 and greater introduced inclusivity as
    // a fourth parameter. '(' or ')' indicates inclusion and
    // '[' or ']' indicates exclusion. The below check allows
    // for inclusion of the startTime but exclusion of the endTime
    return moment().isBetween(startTime, endTime, null, '(]');
}

/**
 *
 * @param {string} localDate a string representing the local date in iso8601DateFormat e.g. "1999-01-31"
 * @returns {string} title case for day of week e.g. "Monday"
 */

export function getDayString(localDate) {
    return toMoment(localDate).toDate().toLocaleString('en-us', { weekday: 'long' }).toUpperCase();
}

export function getPartialPaymentDate(localDate) {
    return getLongDateWithWeekday(toMoment(localDate).subtract(30, 'days'))
}

export function isThirtyDaysLater(date, timezone) {
    const now = moment();
    const thirtyDaysLater = moment.tz(date, timezone).subtract(30, 'day')
    return now.isBefore(thirtyDaysLater)
}

export function matchesYYYYMMDDFormat(dateString) {
    return moment(dateString, 'YYYY-MM-DD', true).isValid()
}

export function addUTCPostfixToISO8601DateString(date) {
    if(!date) return undefined;
    let newDate = date;
    if(matchesYYYYMMDDFormat(date)) {
        newDate = `${newDate}T00:00:00Z`
    }
    return newDate;
}

export function isZeroOrUnitialized(date) {
    const isGolangZero = moment(date).unix() === -62135596800;
    const isUnixZero = moment(date).unix() === 0;
    return !date || isGolangZero || isUnixZero;
}

export function toUTCDateWithoutTime(date) {
    return moment.utc(toLocalDate(date))
}

export const isDayBeforeTodayOnTimezone = (date, timeZone?) => {
    // as moment is a reference for a global time with adjusted views for timezones
    // there is no easy way of comparing dates

    // get today date as YYYY-MM-DD preferably on the facility timezone
    const todayDateOnFacilityOrLocal = (
        timeZone ?
        moment().tz(timeZone) :
        moment()
    ).format(iso8601DateFormat);

    // get argument date as YYYY-MM-DD
    const dateWithoutHourAndTimeZone = date.format(iso8601DateFormat);

    return moment(dateWithoutHourAndTimeZone).isBefore(todayDateOnFacilityOrLocal, 'day');
}

// getRoundUpHourForPayLater calculates the next whole hour that is AT LEAST 30 minutes away from the dateTime
export const getRoundUpHourForPayLater = (dateTime) => {
    const timeToReturn = dateTime ? dateTime.clone() : moment();
    timeToReturn.add(1, 'hour');
    if (timeToReturn.minute() >= 30) timeToReturn.add(1, 'hour');
    return timeToReturn.startOf('hour');
}

// setHour sets the hour of the dateTime and returns the new dateTime moment obj
export const setHour = (dateTime, hour) => {
    const newDateTime = dateTime ? dateTime.clone() : moment();
    newDateTime.set('hour', hour);
    return newDateTime;
}
